import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

export default function MobileBottomSheet({ isOpen, onClose, title, children, footer, height = "85%", fullScreen = false, slideFrom = "bottom", hideHeader = false }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            setTimeout(() => setIsAnimating(true), 10);
        } else {
            setIsAnimating(false);
            setTimeout(() => setIsVisible(false), 300);
        }
    }, [isOpen]);

    if (!isVisible) return null;

    const translateClass = slideFrom === 'right'
        ? (isAnimating ? 'translate-x-0' : 'translate-x-full')
        : (isAnimating ? 'translate-y-0' : 'translate-y-full');

    return createPortal(
        <div className="fixed inset-0 z-[10000]">
            {/* Backdrop (Only for non-full screen or transitional) */}
            <div
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
            ></div>

            {/* Sheet Container */}
            <div
                className={`bg-white shadow-[0_-10px_40px_rgba(0,0,0,0.2)] transform transition-transform duration-300 ease-out flex flex-col 
                ${translateClass} 
                ${fullScreen ? 'fixed inset-0 z-[10001] rounded-none h-full' : 'absolute bottom-0 left-0 right-0 rounded-t-[2rem]'}`}
                style={{ height: fullScreen ? '100%' : height, maxHeight: fullScreen ? 'none' : '95vh' }}
            >
                {/* Drag Handle Area (Hidden on Full Screen) */}
                {!fullScreen && (
                    <div className="w-full flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing" onClick={onClose}>
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full"></div>
                    </div>
                )}

                {/* Header */}
                {!hideHeader && (
                    <div className={`flex justify-between items-center border-b border-gray-50 ${fullScreen ? 'px-4 py-4 mt-safe-top' : 'px-6 pb-4'}`}>
                        {fullScreen ? (
                            // Full Screen Header Style
                            <div className="flex items-center gap-3">
                                <button onClick={onClose} className="p-2 -ml-2 text-gray-800">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                                </button>
                                <h3 className="text-xl font-black text-[#2C3E30] font-do-hyeon">{title}</h3>
                            </div>
                        ) : (
                            <h3 className="text-xl font-black text-[#2C3E30] font-do-hyeon">{title}</h3>
                        )}

                        {!fullScreen && (
                            <button onClick={onClose} className="p-2 bg-gray-100 rounded-full text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        )}
                        {fullScreen && (
                            <button onClick={onClose} className="p-2 text-gray-400">
                                <X size={24} />
                            </button>
                        )}
                    </div>
                )}

                {/* Content - Scrollable */}
                <div className={`flex-1 overflow-y-auto custom-scrollbar hide-scrollbar relative ${hideHeader ? 'p-0' : 'p-6'}`}>
                    {children}
                </div>

                {/* Fixed Footer (if provided) */}
                {footer && (
                    <div className="p-4 border-t border-gray-100 bg-white pb-safe">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
